"use client"

import { useState, useEffect } from "react"
import RoleModal from "../components/RoleModal"
import PermissionModal from "../components/PermissionModal"
import Layout from "../components/Layout"
import { FaEdit, FaTrash, FaPlus, FaShieldAlt, FaUserTag, FaLock, FaKey, FaSearch, FaSync } from "react-icons/fa"

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`

const CustomModal = ({ isOpen, onClose, onConfirm, title, message, type = "confirm" }) => {
  if (!isOpen) return null;

  const getIcon = () => {
    if (type === "success") {
      return (
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
          <svg className="h-8 w-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    }
    if (type === "error") {
      return (
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
          <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    }
    return (
      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-orange-100 mb-4">
        <svg className="h-8 w-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300">
        <div className="p-6">
          {getIcon()}
          <h3 className="text-xl font-bold text-center text-gray-800 mb-2">{title}</h3>
          <p className="text-gray-600 text-center mb-6">{message}</p>
          <div className="flex justify-center space-x-3">
            {type === "confirm" ? (
              <>
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all duration-200 font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={onConfirm}
                  className="px-5 py-2.5 bg-gradient-to-r from-[#5e17eb] to-[#7c3aed] text-white rounded-xl hover:from-[#7c3aed] hover:to-[#5e17eb] transition-all duration-200 font-medium shadow-md"
                >
                  Confirmer
                </button>
              </>
            ) : (
              <button
                onClick={onClose}
                className="px-5 py-2.5 bg-gradient-to-r from-[#5e17eb] to-[#7c3aed] text-white rounded-xl hover:from-[#7c3aed] hover:to-[#5e17eb] transition-all duration-200 font-medium shadow-md"
              >
                OK
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const RolesPermissions = () => {
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false)
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false)
  const [isEditRoleModalOpen, setIsEditRoleModalOpen] = useState(false)
  const [isEditPermissionModalOpen, setIsEditPermissionModalOpen] = useState(false)
  const [roles, setRoles] = useState([])
  const [permissions, setPermissions] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [roleToEdit, setRoleToEdit] = useState(null)
  const [permissionToEdit, setPermissionToEdit] = useState(null)
  const [activeTab, setActiveTab] = useState("permissions")
  const [searchRole, setSearchRole] = useState("")
  const [searchPermission, setSearchPermission] = useState("")
  const [refreshing, setRefreshing] = useState(false)
  const [editPermissionName, setEditPermissionName] = useState("")
  
  const [modal, setModal] = useState({ isOpen: false, type: "confirm", title: "", message: "", onConfirm: null })

  const roleColor = "#5e17eb"
  const permissionColor = "#10B981"

  const getToken = () => localStorage.getItem("access_token")

  const getHeaders = () => ({
    "Content-Type": "application/json",
    "Authorization": `Bearer ${getToken()}`
  })

  const showConfirm = (title, message, onConfirm) => {
    setModal({ isOpen: true, type: "confirm", title, message, onConfirm })
  }

  const showSuccess = (message) => {
    setModal({ isOpen: true, type: "success", title: "Succès !", message, onConfirm: null })
  }

  const showError = (message) => {
    setModal({ isOpen: true, type: "error", title: "Erreur", message, onConfirm: null })
  }

  // Fetch all permissions with pagination
  const fetchAllPermissions = async () => {
    let allPermissions = [];
    let page = 1;
    let hasMore = true;
    const token = getToken();
    
    try {
      while (hasMore) {
        const url = `${API_URL}/permissions/?page=${page}&page_size=100`;
        console.log(`📄 Fetching page ${page}: ${url}`);
        
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) {
          console.error(`❌ Error ${response.status} on page ${page}`);
          break;
        }
        
        const data = await response.json();
        
        let pagePermissions = [];
        if (data.results && Array.isArray(data.results)) {
          pagePermissions = data.results;
          hasMore = data.next !== null;
        } else if (Array.isArray(data)) {
          pagePermissions = data;
          hasMore = false;
        }
        
        allPermissions = [...allPermissions, ...pagePermissions];
        console.log(`✅ Page ${page}: got ${pagePermissions.length} permissions, total: ${allPermissions.length}`);
        
        page++;
        if (page > 50) break;
      }
    } catch (err) {
      console.error("❌ Error fetching permissions:", err);
    }
    
    return allPermissions;
  };

  // Fetch all roles with pagination
  const fetchAllRoles = async () => {
    let allRoles = [];
    let page = 1;
    let hasMore = true;
    const token = getToken();
    
    try {
      while (hasMore) {
        const url = `${API_URL}/roles/?page=${page}&page_size=100`;
        console.log(`📄 Fetching page ${page}: ${url}`);
        
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) {
          console.error(`❌ Error ${response.status} on page ${page}`);
          break;
        }
        
        const data = await response.json();
        
        let pageRoles = [];
        if (data.results && Array.isArray(data.results)) {
          pageRoles = data.results;
          hasMore = data.next !== null;
        } else if (Array.isArray(data)) {
          pageRoles = data;
          hasMore = false;
        }
        
        allRoles = [...allRoles, ...pageRoles];
        console.log(`✅ Page ${page}: got ${pageRoles.length} roles, total: ${allRoles.length}`);
        
        page++;
        if (page > 50) break;
      }
    } catch (err) {
      console.error("❌ Error fetching roles:", err);
    }
    
    return allRoles;
  };

  const fetchRoles = async () => {
    try {
      setLoading(true)
      const allRoles = await fetchAllRoles()
      console.log("📋 Tous les rôles chargés:", allRoles.length)
      setRoles(allRoles)
    } catch (err) {
      console.error("Erreur fetch roles:", err)
      setError("Erreur lors du chargement des rôles: " + err.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchPermissions = async () => {
    try {
      setLoading(true)
      const allPermissions = await fetchAllPermissions()
      console.log("📋 Toutes les permissions chargées:", allPermissions.length)
      setPermissions(allPermissions)
    } catch (err) {
      console.error("Erreur fetch permissions:", err)
      setError("Erreur lors du chargement des permissions: " + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreatePermission = async (permissionName) => {
    try {
      console.log("📤 Création permission:", permissionName)
      const response = await fetch(`${API_URL}/permissions/`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ name: permissionName }),
      })
      
      const responseData = await response.json()
      console.log("📥 Réponse création:", responseData)
      
      if (!response.ok) {
        throw new Error(responseData.name?.[0] || responseData.detail || "Erreur lors de la création")
      }
      
      await fetchPermissions()
      showSuccess(`Permission "${permissionName}" créée avec succès!`)
      return Promise.resolve()
    } catch (err) {
      console.error("❌ Erreur création permission:", err)
      showError(err.message)
      return Promise.reject(err)
    }
  }

  // FIXED: Update permission function
  const handleUpdatePermission = async (id, newName) => {
    if (!newName || !newName.trim()) {
      showError("Le nom de la permission ne peut pas être vide")
      return
    }
    
    try {
      console.log("📤 Mise à jour permission ID:", id, "Nouveau nom:", newName)
      const response = await fetch(`${API_URL}/permissions/${id}/`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({ name: newName.trim() }),
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.name?.[0] || errorData.detail || "Erreur lors de la modification")
      }
      
      await fetchPermissions()
      showSuccess(`Permission modifiée avec succès!`)
    } catch (err) {
      console.error("❌ Erreur mise à jour permission:", err)
      showError(err.message)
    }
  }

  // FIXED: Delete permission function
  const handleDeletePermission = async (id, permissionName) => {
    showConfirm(
      "Confirmation de suppression",
      `Voulez-vous vraiment supprimer la permission "${permissionName}" ?`,
      async () => {
        try {
          console.log("📤 Suppression permission ID:", id)
          const response = await fetch(`${API_URL}/permissions/${id}/`, {
            method: "DELETE",
            headers: getHeaders()
          })
          
          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.detail || "Erreur lors de la suppression")
          }
          
          await fetchPermissions()
          showSuccess(`Permission "${permissionName}" supprimée avec succès!`)
        } catch (err) {
          console.error("❌ Erreur suppression permission:", err)
          showError(err.message)
        }
      }
    )
  }

  const handleCreateRole = async (roleData) => {
    if (!roleData.permissions || roleData.permissions.length === 0) {
      showError("Au moins une permission est requise.")
      return Promise.reject()
    }
    try {
      console.log("📤 Création rôle:", roleData)
      const response = await fetch(`${API_URL}/roles/`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          name: roleData.name,
          permissions: roleData.permissions,
        }),
      })
      
      const responseData = await response.json()
      console.log("📥 Réponse création rôle:", responseData)
      
      if (!response.ok) {
        throw new Error(responseData.name?.[0] || responseData.permissions?.[0] || responseData.detail || "Erreur lors de la création")
      }
      
      await fetchRoles()
      showSuccess(`Rôle "${roleData.name}" créé avec succès!`)
      return Promise.resolve()
    } catch (err) {
      console.error("❌ Erreur création rôle:", err)
      showError(err.message)
      return Promise.reject(err)
    }
  }

  const handleUpdateRole = async (roleData) => {
    if (!roleData.permissions || roleData.permissions.length === 0) {
      showError("Au moins une permission est requise.")
      return Promise.reject()
    }
    try {
      console.log("📤 Mise à jour rôle:", roleData)
      const response = await fetch(`${API_URL}/roles/${roleData.id}/`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({
          name: roleData.name,
          permissions: roleData.permissions,
        }),
      })
      
      const responseData = await response.json()
      console.log("📥 Réponse mise à jour:", responseData)
      
      if (!response.ok) {
        throw new Error(responseData.name?.[0] || responseData.permissions?.[0] || responseData.detail || "Erreur lors de la modification")
      }
      
      await fetchRoles()
      showSuccess(`Rôle "${roleData.name}" modifié avec succès!`)
      return Promise.resolve()
    } catch (err) {
      console.error("❌ Erreur mise à jour rôle:", err)
      showError(err.message)
      return Promise.reject(err)
    }
  }

  const handleDeleteRole = async (id, roleName) => {
    showConfirm(
      "Confirmation de suppression",
      `Voulez-vous vraiment supprimer le rôle "${roleName}" ?`,
      async () => {
        try {
          console.log("📤 Suppression rôle ID:", id)
          const response = await fetch(`${API_URL}/roles/${id}/`, {
            method: "DELETE",
            headers: getHeaders()
          })
          
          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.detail || "Erreur lors de la suppression")
          }
          
          await fetchRoles()
          showSuccess(`Rôle "${roleName}" supprimé avec succès!`)
        } catch (err) {
          console.error("❌ Erreur suppression rôle:", err)
          showError(err.message)
        }
      }
    )
  }

  const refreshAllData = async () => {
    setRefreshing(true)
    await Promise.all([fetchRoles(), fetchPermissions()])
    setRefreshing(false)
  }

  useEffect(() => {
    const token = getToken()
    if (!token) {
      setError("Vous devez être connecté pour accéder à cette page")
      return
    }
    refreshAllData()
  }, [])

  // Open edit permission modal
  const openEditPermissionModal = (permission) => {
    setPermissionToEdit(permission)
    setEditPermissionName(permission.name)
    setIsEditPermissionModalOpen(true)
  }

  // Handle permission update from modal
  const handlePermissionUpdateSubmit = async () => {
    if (!editPermissionName || !editPermissionName.trim()) {
      showError("Le nom de la permission ne peut pas être vide")
      return
    }
    await handleUpdatePermission(permissionToEdit.id, editPermissionName)
    setIsEditPermissionModalOpen(false)
    setPermissionToEdit(null)
    setEditPermissionName("")
  }

  const filteredRoles = (Array.isArray(roles) ? roles : []).filter(role =>
    role?.name?.toLowerCase().includes(searchRole.toLowerCase())
  )

  const filteredPermissions = (Array.isArray(permissions) ? permissions : []).filter(permission =>
    permission?.name?.toLowerCase().includes(searchPermission.toLowerCase())
  )

  const getPermissionColor = (id) => {
    const colors = [
      "bg-emerald-50 text-emerald-800 border-emerald-200",
      "bg-green-50 text-green-800 border-green-200",
      "bg-teal-50 text-teal-800 border-teal-200",
      "bg-emerald-100 text-emerald-800 border-emerald-300",
      "bg-green-100 text-green-800 border-green-300",
      "bg-teal-100 text-teal-800 border-teal-300",
      "bg-lime-100 text-lime-800 border-lime-300",
    ]
    return colors[Math.abs(id) % colors.length]
  }

  return (
    <Layout>
      <div style={styles.page}>
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 style={styles.title}>Gestion des Rôles & Permissions</h1>
            <p style={styles.subtitle}>
              Gérez les rôles et les permissions de votre application.
            </p>
          </div>
          
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="ml-3 text-sm text-red-700">{error}</p>
              </div>
              <button className="text-red-500 hover:text-red-700" onClick={() => setError(null)}>
                ×
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100 transition-all duration-300 hover:shadow-xl">
            <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-5">
              <div className="flex items-center">
                <div className="bg-white bg-opacity-20 p-2 rounded-xl">
                  <FaUserTag className="text-white" size={22} />
                </div>
                <h2 className="ml-3 text-xl font-bold text-white">Gestion des Rôles</h2>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-6">
                Créez et gérez les rôles utilisateurs avec leurs permissions associées.
              </p>
              <button
                onClick={() => setIsRoleModalOpen(true)}
                className="flex items-center text-white px-5 py-2.5 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
                style={{ backgroundColor: roleColor }}
              >
                <FaPlus className="mr-2" size={14} />
                Ajouter un Rôle
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100 transition-all duration-300 hover:shadow-xl">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-5">
              <div className="flex items-center">
                <div className="bg-white bg-opacity-20 p-2 rounded-xl">
                  <FaShieldAlt className="text-white" size={22} />
                </div>
                <h2 className="ml-3 text-xl font-bold text-white">Gestion des Permissions</h2>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-6">
                Définissez les permissions qui peuvent être attribuées aux différents rôles.
              </p>
              <button
                onClick={() => setIsPermissionModalOpen(true)}
                className="flex items-center text-white px-5 py-2.5 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
                style={{ backgroundColor: permissionColor }}
              >
                <FaPlus className="mr-2" size={14} />
                Ajouter une Permission
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab("roles")}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm flex items-center transition-all duration-200 ${
                  activeTab === "roles"
                    ? "border-[#5e17eb] text-[#5e17eb]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <FaUserTag className="mr-2" size={14} />
                Rôles ({Array.isArray(roles) ? roles.length : 0})
              </button>
              <button
                onClick={() => setActiveTab("permissions")}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm flex items-center transition-all duration-200 ${
                  activeTab === "permissions"
                    ? "border-emerald-500 text-emerald-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <FaShieldAlt className="mr-2" size={14} />
                Permissions ({Array.isArray(permissions) ? permissions.length : 0})
              </button>
            </nav>
          </div>

          {activeTab === "roles" && (
            <div className="p-6">
              <div className="mb-4 relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Rechercher un rôle..."
                  value={searchRole}
                  onChange={(e) => setSearchRole(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#5e17eb]"
                />
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Nom du Rôle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Permissions
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredRoles.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-6 py-10 text-center text-gray-500">
                          Aucun rôle trouvé
                        </td>
                      </tr>
                    ) : (
                      filteredRoles.map((role) => (
                        <tr key={role.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-xl bg-indigo-100">
                                <FaKey size={16} style={{ color: roleColor }} />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-semibold text-gray-900">{role.name}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {role.permissions && role.permissions.slice(0, 3).map((perm) => (
                                <span
                                  key={perm.id}
                                  className={`text-xs px-2 py-1 rounded-full border ${getPermissionColor(perm.id)}`}
                                >
                                  {perm.name}
                                </span>
                              ))}
                              {role.permissions && role.permissions.length > 3 && (
                                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                                  +{role.permissions.length - 3}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex justify-center space-x-2">
                              <button
                                onClick={() => {
                                  setRoleToEdit(role)
                                  setIsEditRoleModalOpen(true)
                                }}
                                className="p-2 rounded-lg bg-indigo-100 text-indigo-600 hover:bg-indigo-200 transition-all duration-200"
                                title="Modifier"
                              >
                                <FaEdit size={14} />
                              </button>
                              <button
                                onClick={() => handleDeleteRole(role.id, role.name)}
                                className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-all duration-200"
                                title="Supprimer"
                              >
                                <FaTrash size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "permissions" && (
            <div className="p-6">
              <div className="mb-4 relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Rechercher une permission..."
                  value={searchPermission}
                  onChange={(e) => setSearchPermission(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-emerald-500"
                />
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Nom de la Permission
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredPermissions.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="px-6 py-10 text-center text-gray-500">
                          Aucune permission trouvée
                        </td>
                      </tr>
                    ) : (
                      filteredPermissions.map((permission) => (
                        <tr key={permission.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-xl bg-emerald-100">
                                <FaLock size={16} style={{ color: permissionColor }} />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-semibold text-gray-900">{permission.name}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex justify-center space-x-2">
                              <button
                                onClick={() => openEditPermissionModal(permission)}
                                className="p-2 rounded-lg bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-all duration-200"
                                title="Modifier"
                              >
                                <FaEdit size={14} />
                              </button>
                              <button
                                onClick={() => handleDeletePermission(permission.id, permission.name)}
                                className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-all duration-200"
                                title="Supprimer"
                              >
                                <FaTrash size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {filteredPermissions.length > 0 && (
                <div className="mt-4 text-sm text-gray-500 text-center">
                  Total: {filteredPermissions.length} permission(s) affichée(s)
                </div>
              )}
            </div>
          )}
        </div>

        <RoleModal
          isOpen={isRoleModalOpen}
          setIsOpen={setIsRoleModalOpen}
          onCreate={handleCreateRole}
          availablePermissions={permissions}
          title="Ajouter un Rôle"
        />
        <RoleModal
          isOpen={isEditRoleModalOpen}
          setIsOpen={setIsEditRoleModalOpen}
          onCreate={handleUpdateRole}
          availablePermissions={permissions}
          initialData={roleToEdit}
          title="Modifier un Rôle"
        />
        <PermissionModal
          isOpen={isPermissionModalOpen}
          setIsOpen={setIsPermissionModalOpen}
          onCreate={handleCreatePermission}
        />

        {/* Edit Permission Modal */}
        {isEditPermissionModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300">
              <div className="p-6">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-emerald-100 mb-4">
                  <FaShieldAlt className="h-8 w-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-center text-gray-800 mb-2">Modifier la Permission</h3>
                <p className="text-gray-600 text-center mb-6">
                  Modifiez le nom de la permission
                </p>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de la permission
                  </label>
                  <input
                    type="text"
                    value={editPermissionName}
                    onChange={(e) => setEditPermissionName(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-emerald-500"
                    placeholder="Entrez le nom de la permission"
                  />
                </div>
                
                <div className="flex justify-center space-x-3">
                  <button
                    onClick={() => {
                      setIsEditPermissionModalOpen(false)
                      setPermissionToEdit(null)
                      setEditPermissionName("")
                    }}
                    className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all duration-200 font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handlePermissionUpdateSubmit}
                    className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 font-medium shadow-md"
                  >
                    Modifier
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <CustomModal
          isOpen={modal.isOpen}
          onClose={() => setModal({ ...modal, isOpen: false })}
          onConfirm={modal.onConfirm}
          title={modal.title}
          message={modal.message}
          type={modal.type}
        />
      </div>
    </Layout>
  )
}

const styles = {
  page: {
    background: "#f7f6ff",
    padding: "22px",
    borderRadius: "16px",
    minHeight: "100vh",
  },
  title: {
    fontSize: "26px",
    color: "#1f1f2e",
    marginBottom: "4px",
    fontWeight: "600",
  },
  subtitle: {
    color: "#666",
    marginBottom: "18px",
    fontSize: "14px",
  },
}

export default RolesPermissions